import { Button, Card, CardContent, Grid } from '@material-ui/core'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux'
import defaultThumb from '../../../assets/img/default-program.png'
import { PROGRAM_UPDATE_RESET } from '../../../constants/programConstants'
import ModalPopUp from '../../ModalPopUp/ModalPopUp'
import AddEditProgram from '../../Program/AddEditProgram'
import ToastMessage from '../../ToastMessage/ToastMessage'

interface ProgramSummaryProps {
  program: Program
  qty: number
}

const ProgramSummary = ({ program, qty }: ProgramSummaryProps) => {
  const dispatch = useDispatch()

  const [details, setDetails] = useState<Program | undefined>(undefined)
  const [showEditProgram, setShowEditProgram] = useState(false)
  const [toast, setToast] = useState(false)

  const programUpdate = useSelector(
    (state: RootStateOrAny) => state.programUpdate
  )
  const { success: programUpdateSuccess, program: updatedProgram } =
    programUpdate

  const { userInfo } = useSelector((state: RootStateOrAny) => state.userLogin)

  useEffect(() => {
    const config = {
      headers: {
        Authorization: `Bearer ${userInfo.token}`,
      },
    }

    const getProgramDetails = async () => {
      try {
        const { data } = await axios.get(`/api/programs/${program}`, config)
        setDetails(data)
      } catch (error) {
        console.error(error)
      }
    }

    if (!details) {
      getProgramDetails()
    }
  }, [details, program, userInfo])

  useEffect(() => {
    if (programUpdateSuccess) {
      setToast(true)
      dispatch({ type: PROGRAM_UPDATE_RESET })
    }
  }, [programUpdateSuccess, setToast, dispatch, updatedProgram])

  return (
    <>
      {details && (
        <Card className="summary-card">
          <div className="flex space-between header align-center">
            <strong>Program | {details.name}</strong>
            <Button color="primary" onClick={() => setShowEditProgram(true)}>
              <span className="underline"> Edit</span>
            </Button>
          </div>

          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <div className={'no-thumbnail'}>
                  <div
                    className={`thumbnail`}
                    style={{
                      backgroundImage: `url(${defaultThumb})`,
                    }}
                  ></div>
                </div>
              </Grid>

              <Grid item xs={9}>
                <Grid container className="items">
                  <Grid item xs={3}>
                    Make
                  </Grid>
                  <Grid item xs={9}>
                    {details.madeByHexagon ? 'Hexagon' : 'Not by Hexagon'}
                  </Grid>
                  <Grid item xs={3}>
                    Run time
                  </Grid>
                  <Grid item xs={9}>
                    {details && details.runtime.hours} hours{' '}
                    {details && details.runtime.minutes} mins
                  </Grid>
                  <Grid item xs={3}>
                    Machine
                  </Grid>
                  <Grid item xs={9}>
                    {details.machineModel}
                  </Grid>
                  <Grid item xs={3}>
                    Fixture required
                  </Grid>
                  <Grid item xs={9}>
                    {details.fixtureRequired === true ? 'Yes' : 'No'}
                  </Grid>
                  {details.fixtureRequired && (
                    <>
                      <Grid item xs={3}>
                        Fixture type
                      </Grid>
                      <Grid item xs={9}>
                        {details.fixtureType}
                      </Grid>
                    </>
                  )}
                  <Grid item xs={3}>
                    Part quantity
                  </Grid>
                  <Grid item xs={9}>
                    {qty}
                  </Grid>
                </Grid>

                <div className="strong m-t-24">Files</div>

                <Grid container className="items" spacing={0}>
                  <Grid item xs={3}>
                    Program files
                  </Grid>
                  <Grid item xs={9}>
                    {details.programFiles &&
                      details.programFiles.map((file, index) => (
                        <div key={index}>{file.name}</div>
                      ))}
                  </Grid>
                  {details.partProgramLink !== '' && (
                    <>
                      <Grid item xs={3}>
                        Part program link
                      </Grid>
                      <Grid item xs={9}>
                        {details.partProgramLink}
                      </Grid>
                    </>
                  )}
                  <Grid item xs={3}>
                    Additional files
                  </Grid>
                  <Grid item xs={9}>
                    {details.additionalFiles &&
                      details.additionalFiles.map((file, index) => (
                        <div key={index}>{file.name}</div>
                      ))}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      <ModalPopUp
        title="Edit program"
        variant="full"
        setShowModal={setShowEditProgram}
        showModal={showEditProgram}
      >
        <AddEditProgram
          setShowAddProgram={setShowEditProgram}
          programToEdit={details}
          setProgramToEdit={setDetails}
        />
      </ModalPopUp>
      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Program updated</p>
            <p className="fs14 m-b-0">The program was successfully updated.</p>
          </>
        }
        open={toast}
        setOpen={setToast}
      />
    </>
  )
}

export default ProgramSummary
